import { useState, type FormEvent } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';

const milestones = [
  { year: '2018', title: '品牌创立', description: '亚洲精品在香港成立，创始团队走访日韩、东南亚等地，开始建立品牌合作网络。' },
  { year: '2019', title: '首批精选', description: '完成首批500个品牌的筛选与签约，涵盖时尚、美妆、家居三大品类。' },
  { year: '2020', title: '线上平台上线', description: '亚洲精品官方网站正式上线，为消费者提供便捷的线上精品选购体验。' },
  { year: '2021', title: '品类拓展', description: '新增美食珍馐和匠心工艺品类，合作品牌突破2000家。' },
  { year: '2022', title: '六道甄选体系', description: '建立并完善六道甄选流程标准，确保每件商品都经过严格筛选。' },
  { year: '2023', title: '会员突破20万', description: '凭借严格的选品和优质的服务，亚洲精品会员数量突破20万。' },
  { year: '2024', title: '国际化布局', description: '在新加坡、东京、首尔设立办事处，深化与亚洲各地品牌的合作。' },
  { year: '2025', title: '亚洲奢品上线', description: '推出亚洲奢品专区，与顶级奢侈品牌合作，会员数突破50万。' },
  { year: '2026', title: '持续创新', description: '持续挖掘亚洲各地的新兴品牌与匠人工坊，不断丰富平台精品品类。' },
];

const testimonials = [
  {
    name: '林女士',
    location: '上海',
    text: '在亚洲精品找到了很多之前不知道的小众日本品牌，每件商品都包装精美，品质超出预期。已经成为忠实会员两年了！',
    rating: 5,
  },
  {
    name: '张先生',
    location: '北京',
    text: '送给太太的景德镇茶具套装，她非常喜欢。亚洲精品的选品眼光确实独到，每件商品都经得起推敲。',
    rating: 5,
  },
  {
    name: '陈女士',
    location: '新加坡',
    text: '作为海外华人，亚洲精品让我能够方便地购买到来自亚洲各地的优质商品，配送也很及时，非常推荐。',
    rating: 5,
  },
  {
    name: '王先生',
    location: '深圳',
    text: '美食区的台湾高山茶和北海道巧克力都是我的最爱，每次有新品都会第一时间下单。品质始终如一。',
    rating: 5,
  },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot?.value.trim()) {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9tvj8lca23r0tr1ani0', {
        method: 'POST',
        body: formData,
      });
      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于亚洲精品',
            description: '亚洲精品汇聚日韩、东南亚、中华等顶级品牌与匠心工艺，为您精选最具品质代表性的亚洲精品。',
            url: 'https://pcnqq.cn/about',
          }),
        }}
      />

      <main>
        <section className="relative h-52 md:h-80 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20Asian%20minimalist%20workspace%20with%20traditional%20and%20modern%20elements%2C%20warm%20wooden%20furniture%2C%20handcrafted%20artifacts%20on%20display%20shelves%2C%20soft%20natural%20light%20through%20large%20windows%2C%20sophisticated%20brand%20headquarters%20atmosphere&width=1920&height=1080&seq=about-hero-01&orientation=landscape"
            alt="亚洲精品 - 关于我们"
            title="亚洲精品品牌介绍"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/20" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="max-w-7xl mx-auto">
              <h1 className="text-xl md:text-3xl font-heading font-bold text-white">
                关于亚洲精品
              </h1>
              <p className="text-sm text-white/70 mt-1">
                汇聚东方美学，品味精致生活
              </p>
            </div>
          </div>
        </section>

        <Breadcrumb />

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <p className="text-sm text-primary-500 font-medium tracking-wider mb-3">品牌故事</p>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                  从热爱到使命
                </h2>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                  亚洲精品的诞生，源于创始团队对东方美学的深深热爱。2018年，一群来自亚洲各地的设计师、文化研究者与品质生活家聚集在一起，他们有一个共同的困惑：为什么亚洲拥有如此丰富的顶级品牌和匠人工坊，却没有一个真正值得信赖的精品平台将它们呈现给世界？
                </p>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                  于是，亚洲精品应运而生。我们花了整整一年时间，走访了日本京都的丝绸老铺、韩国首尔的独立设计工作室、中国景德镇的千年瓷窑、泰国清迈的手工艺村落、越南河内的漆器工坊...只为了建立一套严格而公正的选品体系。
                </p>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                  我们的使命很简单：让每一件承载着文化与匠心的亚洲精品，都能被真正懂得欣赏它的人发现。
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-primary-50 rounded-xl p-5 md:p-6 text-center">
                  <p className="text-3xl md:text-4xl font-heading font-bold text-primary-500">500+</p>
                  <p className="text-xs md:text-sm text-foreground-600 mt-2">精选品牌</p>
                </div>
                <div className="bg-accent-50 rounded-xl p-5 md:p-6 text-center">
                  <p className="text-3xl md:text-4xl font-heading font-bold text-accent-500">10,000+</p>
                  <p className="text-xs md:text-sm text-foreground-600 mt-2">甄选精品</p>
                </div>
                <div className="bg-secondary-50 rounded-xl p-5 md:p-6 text-center">
                  <p className="text-3xl md:text-4xl font-heading font-bold text-secondary-500">50万+</p>
                  <p className="text-xs md:text-sm text-foreground-600 mt-2">信赖会员</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 md:p-6 text-center">
                  <p className="text-3xl md:text-4xl font-heading font-bold text-foreground-800">6</p>
                  <p className="text-xs md:text-sm text-foreground-600 mt-2">道甄选流程</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                发展历程
              </h2>
              <p className="mt-3 text-foreground-500 text-sm md:text-base">
                亚洲精品的每一步成长
              </p>
            </div>

            <div className="relative">
              <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-background-300/60 transform -translate-x-1/2" />
              <div className="space-y-0">
                {milestones.map((item, index) => (
                  <div
                    key={item.year}
                    className={`flex flex-col lg:flex-row items-start gap-4 lg:gap-8 mb-8 lg:mb-0 ${
                      index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                      <div className="bg-background-50 rounded-xl p-5">
                        <span className="text-xs font-semibold text-primary-500 bg-primary-50 px-2.5 py-1 rounded-full whitespace-nowrap">
                          {item.year}
                        </span>
                        <h3 className="text-base font-heading font-bold text-foreground-900 mt-2 mb-1">
                          {item.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                    <div className="hidden lg:flex items-center justify-center w-8 flex-shrink-0">
                      <div className="w-3 h-3 rounded-full bg-primary-500 ring-4 ring-primary-100" />
                    </div>
                    <div className="flex-1 hidden lg:block" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="reputation" className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                口碑评价
              </h2>
              <p className="mt-3 text-foreground-500 text-sm md:text-base">
                来自亚洲各地会员的真实反馈
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((t) => (
                <div key={t.name} className="bg-background-100 rounded-xl p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill w-4 h-4 flex items-center justify-center text-primary-400"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{t.text}"
                  </p>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-400">{t.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                联系我们
              </h2>
              <p className="mt-3 text-foreground-500 text-sm md:text-base">
                如有任何问题或合作意向，请随时联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mx-auto mb-3">
                  <i className="ri-phone-line text-xl w-5 h-5 flex items-center justify-center"></i>
                </div>
                <p className="text-sm font-medium text-foreground-900">客服热线</p>
                <p className="text-xs text-foreground-500 mt-1">400-888-9999</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mx-auto mb-3">
                  <i className="ri-mail-line text-xl w-5 h-5 flex items-center justify-center"></i>
                </div>
                <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                <p className="text-xs text-foreground-500 mt-1">service@yazhoujingpin.com</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mx-auto mb-3">
                  <i className="ri-time-line text-xl w-5 h-5 flex items-center justify-center"></i>
                </div>
                <p className="text-sm font-medium text-foreground-900">工作时间</p>
                <p className="text-xs text-foreground-500 mt-1">周一至周五 09:00-18:00</p>
              </div>
            </div>

            <form
              className="bg-background-50 rounded-xl p-6 md:p-8"
              data-readdy-form
              onSubmit={handleSubmit}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题 <span className="text-accent-500">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                  placeholder="请输入咨询主题"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                />
                <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
              </div>

              <input
                type="text"
                name="phone_alt"
                className="spam-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {formError && (
                <div className="mb-4 p-3 bg-accent-100/80 text-accent-700 text-sm rounded-lg">
                  {formError}
                </div>
              )}

              {formStatus === 'success' && (
                <div className="mb-4 p-3 bg-primary-100 text-primary-700 text-sm rounded-lg">
                  感谢您的留言！我们会在24小时内回复您。
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'submitting' ? '提交中...' : '发送留言'}
              </button>
            </form>
          </div>
        </section>
      </main>
    </>
  );
}